import { useEffect, useMemo, useState } from 'react';
import { Map, AdvancedMarker, Pin, InfoWindow, useMap } from '@vis.gl/react-google-maps';
import skinclusiveBadge from '../assets/skinclusive-badge.png';

const DEFAULT_ZOOM = 11;
const HIGHLIGHT_ZOOM = 14;

/**
 * Google Map rendering the visible providers as markers.
 * Recenters + auto-fits bounds when the visible list changes.
 * Two-way highlight sync with the list: clicking a marker opens an info window,
 * and `highlightedKey` from the parent shifts the active marker too.
 */
export default function ProviderMap({ providers, center, highlightedKey, onHighlight }) {
  return (
    <Map
      defaultCenter={center}
      defaultZoom={DEFAULT_ZOOM}
      mapId="provider-finder-map"
      gestureHandling="cooperative"
      disableDefaultUI={false}
      className="pf-map"
    >
      <FitBoundsToMarkers providers={providers} center={center} highlightedKey={highlightedKey} />
      {providers.map(p => {
        const key = providerKey(p);
        const active = key === highlightedKey;
        return (
          <ProviderMarker
            key={key}
            provider={p}
            active={active}
            onClick={() => onHighlight(active ? null : key)}
            onClose={() => onHighlight(null)}
          />
        );
      })}
    </Map>
  );
}

function ProviderMarker({ provider, active, onClick, onClose }) {
  return (
    <>
      <AdvancedMarker
        position={{ lat: provider.lat, lng: provider.lng }}
        onClick={onClick}
        title={provider.store_name}
      >
        <Pin
          background={active ? '#CC464B' : '#FF585d'}
          borderColor="#7a1a30"
          glyphColor="#fff"
        />
      </AdvancedMarker>
      {active && (
        <InfoWindow
          position={{ lat: provider.lat, lng: provider.lng }}
          pixelOffset={[0, -38]}
          onCloseClick={onClose}
          headerDisabled
          maxWidth={280}
        >
          <div className="pf-infowindow">
            <strong className="pf-infowindow__name">{provider.store_name}</strong>
            {provider.is_skinclusive && (
              <img
                className="pf-infowindow__skinclusive"
                src={skinclusiveBadge}
                alt="Skinclusive Provider"
                width="120"
                height="48"
                style={{ display: 'block', maxWidth: '120px', height: 'auto', margin: '4px 0 8px' }}
              />
            )}
            <div className="pf-infowindow__address">{provider.address}</div>
            {provider.phone && (
              <a className="pf-infowindow__phone" href={`tel:${provider.phone}`}>
                {provider.phone}
              </a>
            )}
            {provider.web && (
              <a
                className="pf-infowindow__web"
                href={provider.web}
                target="_blank"
                rel="noopener noreferrer"
              >
                Visit Website
              </a>
            )}
            {/* Device list intentionally omitted — Catherine flagged 2026-05-18
                that listing devices per practice in the public popup makes it
                easy for competitors to scrape what each customer owns. */}
          </div>
        </InfoWindow>
      )}
    </>
  );
}

/**
 * Adjusts the map viewport to fit all currently-visible markers + the search center.
 * Runs whenever the visible list changes. When a marker is highlighted, zooms in.
 */
function FitBoundsToMarkers({ providers, center, highlightedKey }) {
  const map = useMap();
  const positions = useMemo(
    () => providers.map(p => ({ lat: p.lat, lng: p.lng, key: providerKey(p) })),
    [providers]
  );

  useEffect(() => {
    if (!map || !center) return;

    if (highlightedKey) {
      const hit = positions.find(p => p.key === highlightedKey);
      if (hit) {
        map.panTo({ lat: hit.lat, lng: hit.lng });
        map.setZoom(HIGHLIGHT_ZOOM);
        return;
      }
    }

    if (positions.length === 0) {
      map.panTo(center);
      map.setZoom(DEFAULT_ZOOM);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    bounds.extend(center);
    for (const p of positions) bounds.extend({ lat: p.lat, lng: p.lng });
    map.fitBounds(bounds, 64);
  }, [map, positions, center, highlightedKey]);

  return null;
}

export function providerKey(p) {
  return `${p.store_name}|${p.lat},${p.lng}`;
}
